import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Plus, Briefcase, Search, Globe, Link2, RefreshCw, QrCode, MoreHorizontal,
  ChevronLeft, ChevronRight, RotateCcw, Settings2, XCircle, X, Loader2, AlertTriangle,
} from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { getMentionableUsers, getBatchReleasePreview, batchClosePositions } from '../api';
import {
  Position, PositionStatus, POSITION_STATUS_LABELS, User,
  BatchReleasePreview, BatchCloseResult,
} from '../types';
import {
  PRIORITY_OPTIONS, PriorityBadge, PositionStatusBadge, Avatar, fmtDate,
  useDepartments, SearchableSelect,
} from './positionShared';
import CreatePositionDrawer from './CreatePositionDrawer';

interface Props {
  positions: Position[];
  onUpdate: () => void;
  currentUser: User;
  notificationBell?: React.ReactNode;
  /** 打开岗位详情页；tab 可指定初始 Tab（如创建后直接进「编制绑定」） */
  onOpenPosition: (id: string, tab?: string) => void;
}

type TabKey = 'all' | 'mine' | 'shared' | 'open' | 'draft' | 'closed';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'all', label: '全部岗位' },
  { key: 'mine', label: '我负责的' },
  { key: 'shared', label: '共享给我的' },
  { key: 'open', label: '招聘中' },
  { key: 'draft', label: '草稿' },
  { key: 'closed', label: '已关闭' },
];

const PAGE_SIZE = 10;

/** 已关闭的岗位没有再关一次的意义，批量选择时排除 */
const isClosable = (p: Position) => p.status !== 'closed';

export default function PositionManager({ positions, onUpdate, currentUser, notificationBell, onOpenPosition }: Props) {
  const isHr = currentUser.role === 'admin' || currentUser.role === 'hr' || currentUser.role === 'vip';

  const [tab, setTab] = useState<TabKey>('all');
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [page, setPage] = useState(1);

  const [showDrawer, setShowDrawer] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [showCareerPanel, setShowCareerPanel] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);

  const deptHook = useDepartments(isHr);
  const [owners, setOwners] = useState<{ id: string; name: string }[]>([]);

  // Career portal settings（沿用原有接口与交互，功能不变）
  const [careerToken, setCareerToken] = useState('');
  const [companyName, setCompanyName] = useState('');

  useEffect(() => {
    if (!isHr) return;
    fetch('/api/positions/career-settings', {
      headers: { Authorization: `Bearer ${localStorage.getItem('ats_token')}` },
    })
      .then(r => r.json())
      .then(d => { setCareerToken(d.career_token || ''); setCompanyName(d.company_name || ''); })
      .catch(() => {});
    getMentionableUsers()
      .then((list: any[]) => setOwners(Array.isArray(list) ? list.map(u => ({ id: u.id, name: u.name || u.username })) : []))
      .catch(() => {});
  }, [isHr]);

  const closeMore = useCallback((e: MouseEvent) => {
    if (moreRef.current && !moreRef.current.contains(e.target as Node)) setShowMore(false);
  }, []);
  useEffect(() => {
    document.addEventListener('mousedown', closeMore);
    return () => document.removeEventListener('mousedown', closeMore);
  }, [closeMore]);

  const careerUrl = careerToken ? `${window.location.origin}/career/${careerToken}` : '';

  const handleSaveCareerSettings = async () => {
    try {
      const res = await fetch('/api/positions/career-settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('ats_token')}` },
        body: JSON.stringify({ company_name: companyName }),
      });
      const data = await res.json();
      setCareerToken(data.career_token);
      toast.success('已保存');
    } catch { toast.error('保存失败'); }
  };

  const handleRegenerateToken = async () => {
    if (!window.confirm('重新生成链接后，旧链接将失效。确认？')) return;
    try {
      const res = await fetch('/api/positions/career-settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('ats_token')}` },
        body: JSON.stringify({ regenerate_token: true }),
      });
      const data = await res.json();
      setCareerToken(data.career_token);
      toast.success('链接已更新');
    } catch { toast.error('失败'); }
  };

  /** Tab 归属判定：closed Tab 同时收纳 paused，列表用 Badge 区分两者 */
  const matchTab = useCallback((p: Position, key: TabKey) => {
    switch (key) {
      case 'mine': return p.owner_id === currentUser.id;
      case 'shared': return !!p.shared_with_me && p.owner_id !== currentUser.id;
      case 'open': return p.status === 'open';
      case 'draft': return p.status === 'draft';
      case 'closed': return p.status === 'closed' || p.status === 'paused';
      default: return true;
    }
  }, [currentUser.id]);

  const tabCounts = useMemo(() => {
    const counts = {} as Record<TabKey, number>;
    TABS.forEach(t => { counts[t.key] = positions.filter(p => matchTab(p, t.key)).length; });
    return counts;
  }, [positions, matchTab]);

  const filtered = useMemo(() => positions.filter(p => {
    if (!matchTab(p, tab)) return false;
    if (deptFilter && p.department !== deptFilter) return false;
    if (statusFilter && p.status !== statusFilter) return false;
    if (priorityFilter && (p.priority || '') !== priorityFilter) return false;
    if (ownerFilter && p.owner_id !== ownerFilter) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      return [p.name, p.code, p.department, p.background, p.location]
        .some(v => (v || '').toLowerCase().includes(q));
    }
    return true;
  }), [positions, tab, deptFilter, statusFilter, priorityFilter, ownerFilter, search, matchTab]);

  // 筛选条件变化后停留在空白页会看不到数据，统一回到第一页
  useEffect(() => { setPage(1); }, [tab, deptFilter, statusFilter, priorityFilter, ownerFilter, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const curPage = Math.min(page, totalPages);
  const rows = filtered.slice((curPage - 1) * PAGE_SIZE, curPage * PAGE_SIZE);

  const hasFilter = !!(search || deptFilter || statusFilter || priorityFilter || ownerFilter);
  const resetFilters = () => {
    setSearch(''); setDeptFilter(''); setStatusFilter(''); setPriorityFilter(''); setOwnerFilter('');
  };

  // ===== 批量关闭 =====
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [preview, setPreview] = useState<BatchReleasePreview | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [closing, setClosing] = useState(false);

  // 切 Tab / 改筛选后原选中项可能已不在视野内，继续批量操作会误关，直接清空
  useEffect(() => { setSelected(new Set()); }, [tab, deptFilter, statusFilter, priorityFilter, ownerFilter, search]);

  const selectableRows = useMemo(() => rows.filter(isClosable), [rows]);
  const allPageSelected = selectableRows.length > 0 && selectableRows.every(p => selected.has(p.id));

  const toggleOne = (id: string) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  /** 全选只作用于当前页可关闭的行，避免一键选中上百个看不见的岗位 */
  const togglePage = () => setSelected(prev => {
    const next = new Set(prev);
    if (allPageSelected) selectableRows.forEach(p => next.delete(p.id));
    else selectableRows.forEach(p => next.add(p.id));
    return next;
  });

  const selectedIds = useMemo(() => Array.from(selected), [selected]);

  const openBatchConfirm = async () => {
    if (selectedIds.length === 0) return;
    setShowConfirm(true);
    setPreview(null);
    try {
      setPreview(await getBatchReleasePreview(selectedIds));
    } catch {
      // 预览失败不阻断关闭，弹窗上用兜底文案代替编制明细
      setPreview({ items: [], release_total: 0, keep_total: 0 });
    }
  };

  const runBatchClose = async () => {
    setClosing(true);
    try {
      const result: BatchCloseResult = await batchClosePositions(selectedIds);
      const released = result.closed.reduce((n, c) => n + c.released.length, 0);
      const kept = result.closed.reduce((n, c) => n + c.kept.length, 0);
      if (result.closed.length > 0) {
        toast.success(
          `已关闭 ${result.closed.length} 个岗位`
          + (released ? `，释放 ${released} 个编制` : '')
          + (kept ? `，${kept} 个编制因 Offer 流转/已入职保留` : '')
        );
      }
      if (result.skipped.length > 0) toast(`${result.skipped.length} 个岗位本就已关闭，已跳过`);
      // 逐条报失败原因：批量里失败的通常各有各的原因，汇总成一句话反而看不出问题
      result.failed.forEach(f => toast.error(`「${f.name}」关闭失败：${f.error}`));
      setSelected(new Set());
      setShowConfirm(false);
      onUpdate();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || '批量关闭失败');
    } finally {
      setClosing(false);
    }
  };

  const openCount = tabCounts.open || 0;
  const draftCount = tabCounts.draft || 0;

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="shrink-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <Briefcase size={20} className="text-primary-500" />
            岗位管理
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            共 {positions.length} 个岗位 · {openCount} 招聘中
            {draftCount > 0 && <span> · {draftCount} 草稿</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {notificationBell}
          {isHr && (
            <>
              <button onClick={() => setShowDrawer(true)}
                className="h-9 px-4 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg flex items-center gap-1.5 transition-colors">
                <Plus size={14} />创建岗位
              </button>
              <div ref={moreRef} className="relative">
                <button onClick={() => setShowMore(!showMore)}
                  className="h-9 w-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50">
                  <MoreHorizontal size={16} />
                </button>
                {showMore && (
                  <div className="absolute right-0 mt-1 w-44 bg-white rounded-lg border border-gray-200 shadow-lg z-40 py-1">
                    <button onClick={() => { setShowCareerPanel(v => !v); setShowMore(false); }}
                      className="w-full px-3 py-2 text-xs text-left text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                      <Settings2 size={13} className="text-gray-400" />招聘门户设置
                    </button>
                    {careerUrl && (
                      <button onClick={() => { window.open(careerUrl, '_blank'); setShowMore(false); }}
                        className="w-full px-3 py-2 text-xs text-left text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                        <Globe size={13} className="text-gray-400" />预览招聘门户
                      </button>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {/* 招聘门户设置（原有逻辑整体保留，收进「更多」菜单按需展开） */}
        {isHr && showCareerPanel && (
          <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <Globe size={15} className="text-indigo-500" />
                招聘门户（公开收集简历）
              </h2>
              <button onClick={() => setShowCareerPanel(false)} className="text-xs text-indigo-600 hover:text-indigo-800">收起</button>
            </div>
            <div className="mt-4 space-y-3">
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-500 w-16 shrink-0">公司名称</label>
                <input value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="展示在招聘页顶部"
                  className="h-8 px-3 rounded-lg border border-gray-200 text-sm flex-1 focus:outline-none focus:ring-1 focus:ring-indigo-500" />
                <button onClick={handleSaveCareerSettings}
                  className="h-8 px-3 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">保存</button>
              </div>
              {careerUrl && (
                <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-3">
                  <Link2 size={14} className="text-gray-400 shrink-0" />
                  <input value={careerUrl} readOnly className="flex-1 text-xs text-gray-600 bg-transparent border-none outline-none" />
                  <button onClick={() => { navigator.clipboard.writeText(careerUrl); toast.success('链接已复制'); }}
                    className="text-xs text-indigo-600 hover:text-indigo-800 font-medium whitespace-nowrap">复制</button>
                  <button onClick={() => window.open(careerUrl, '_blank')}
                    className="text-xs text-indigo-600 hover:text-indigo-800 font-medium whitespace-nowrap">预览</button>
                  <button onClick={() => window.open(`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(careerUrl)}`, '_blank')}
                    className="text-xs text-indigo-600 hover:text-indigo-800 font-medium whitespace-nowrap flex items-center gap-0.5"><QrCode size={12} />二维码</button>
                </div>
              )}
              <div className="flex items-center justify-between">
                <p className="text-[11px] text-gray-400">将岗位设为「公开」后，会展示在招聘门户上供外部投递</p>
                <button onClick={handleRegenerateToken} className="text-[11px] text-gray-400 hover:text-red-500 flex items-center gap-1">
                  <RefreshCw size={10} />重新生成链接
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl border border-gray-200">
          {/* Tabs */}
          <div className="px-5 pt-1 border-b border-gray-200 flex items-center gap-5 overflow-x-auto">
            {TABS.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={clsx('py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors flex items-center gap-1.5',
                  tab === t.key ? 'text-primary-600 border-primary-600' : 'text-gray-500 border-transparent hover:text-gray-700')}>
                {t.label}
                <span className={clsx('text-[10px] font-bold min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center',
                  tab === t.key ? 'bg-primary-100 text-primary-600' : 'bg-gray-100 text-gray-400')}>
                  {tabCounts[t.key] ?? 0}
                </span>
              </button>
            ))}
          </div>

          {/* Filters */}
          <div className="px-5 py-3 border-b border-gray-100 flex flex-wrap items-center gap-2">
            <div className="relative w-64">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="搜索岗位名称、编号、关键词"
                className="w-full h-9 pl-9 pr-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
            </div>
            <SearchableSelect
              value={deptFilter}
              options={deptHook.options.filter(d => d.level === undefined || d.level === 1).map(d => ({ label: d.name, value: d.name }))}
              placeholder="全部部门" emptyLabel="全部部门" searchPlaceholder="搜索部门..."
              onChange={setDeptFilter} className="w-40"
            />
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              className="h-9 px-3 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-primary-500">
              <option value="">全部状态</option>
              {(Object.keys(POSITION_STATUS_LABELS) as PositionStatus[]).map(s => (
                <option key={s} value={s}>{POSITION_STATUS_LABELS[s]}</option>
              ))}
            </select>
            <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)}
              className="h-9 px-3 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-primary-500">
              <option value="">全部优先级</option>
              {PRIORITY_OPTIONS.filter(o => o.value).map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <select value={ownerFilter} onChange={e => setOwnerFilter(e.target.value)}
              className="h-9 px-3 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-primary-500">
              <option value="">全部负责人</option>
              {owners.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
            {hasFilter && (
              <button onClick={resetFilters}
                className="h-9 px-3 text-xs text-gray-500 hover:text-primary-600 border border-gray-200 rounded-lg flex items-center gap-1">
                <RotateCcw size={12} />重置
              </button>
            )}
          </div>

          {/* 批量操作条：仅在有选中项时占位，避免常态下多一行空白 */}
          {isHr && selectedIds.length > 0 && (
            <div className="px-5 py-2.5 bg-primary-50 border-b border-primary-100 flex items-center gap-3">
              <span className="text-xs text-gray-700">
                已选 <span className="font-semibold text-primary-600">{selectedIds.length}</span> 个岗位
              </span>
              <button onClick={openBatchConfirm}
                className="h-8 px-3 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg flex items-center gap-1.5 transition-colors">
                <XCircle size={13} />批量关闭岗位
              </button>
              <button onClick={() => setSelected(new Set())}
                className="h-8 px-3 text-xs text-gray-500 hover:text-gray-700 border border-gray-200 bg-white rounded-lg">
                取消选择
              </button>
              <span className="text-[11px] text-gray-400 ml-auto">关闭后会释放绑定的空闲编制，Offer 流转中与已入职占用的编制会保留</span>
            </div>
          )}

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 bg-gray-50">
                  {isHr && (
                    <th className="w-10 px-3 py-2.5">
                      <input type="checkbox" checked={allPageSelected} onChange={togglePage}
                        disabled={selectableRows.length === 0}
                        title={selectableRows.length === 0 ? '本页没有可关闭的岗位' : '全选本页'}
                        className="w-3.5 h-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer disabled:cursor-not-allowed" />
                    </th>
                  )}
                  <th className="text-left font-medium px-5 py-2.5 whitespace-nowrap">岗位名称</th>
                  <th className="text-left font-medium px-3 py-2.5 whitespace-nowrap">岗位编号</th>
                  <th className="text-left font-medium px-3 py-2.5 whitespace-nowrap">所属部门</th>
                  <th className="text-left font-medium px-3 py-2.5 whitespace-nowrap">招聘负责人</th>
                  <th className="text-left font-medium px-3 py-2.5 whitespace-nowrap">优先级</th>
                  <th className="text-left font-medium px-3 py-2.5 whitespace-nowrap">招聘状态</th>
                  <th className="text-center font-medium px-3 py-2.5 whitespace-nowrap">绑定编制</th>
                  <th className="text-center font-medium px-3 py-2.5 whitespace-nowrap">已使用</th>
                  <th className="text-center font-medium px-3 py-2.5 whitespace-nowrap">候选人数</th>
                  <th className="text-center font-medium px-3 py-2.5 whitespace-nowrap">Offer</th>
                  <th className="text-left font-medium px-3 py-2.5 whitespace-nowrap">发布时间</th>
                  <th className="text-right font-medium px-5 py-2.5 whitespace-nowrap">操作</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={isHr ? 13 : 12} className="px-5 py-16 text-center text-sm text-gray-400">
                      {hasFilter ? '没有匹配的岗位' : '暂无岗位'}
                    </td>
                  </tr>
                ) : rows.map(p => (
                  <tr key={p.id} onClick={() => onOpenPosition(p.id)}
                    className={clsx('border-t border-gray-100 cursor-pointer',
                      selected.has(p.id) ? 'bg-primary-50/60 hover:bg-primary-50' : 'hover:bg-gray-50')}>
                    {isHr && (
                      // 勾选不应触发行点击跳详情
                      <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                        <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggleOne(p.id)}
                          disabled={!isClosable(p)}
                          title={!isClosable(p) ? '岗位已关闭' : ''}
                          className="w-3.5 h-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40" />
                      </td>
                    )}
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="font-medium text-gray-800 truncate max-w-[180px]">{p.name}</span>
                        {p.is_public ? <Globe size={11} className="text-indigo-500 shrink-0" /> : null}
                      </div>
                      {p.location && <div className="text-[11px] text-gray-400 mt-0.5 truncate max-w-[180px]">{p.location}</div>}
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-500 whitespace-nowrap">{p.code || '—'}</td>
                    <td className="px-3 py-3 text-xs text-gray-600 whitespace-nowrap">
                      {p.department || '未分配'}
                      {p.sub_department && <span className="text-gray-400"> / {p.sub_department}</span>}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      {p.owner_name ? (
                        <div className="flex items-center gap-1.5">
                          <Avatar name={p.owner_name} size={22} />
                          <span className="text-xs text-gray-700">{p.owner_name}</span>
                        </div>
                      ) : <span className="text-xs text-gray-300">未指派</span>}
                    </td>
                    <td className="px-3 py-3"><PriorityBadge value={p.priority} /></td>
                    <td className="px-3 py-3"><PositionStatusBadge status={p.status} /></td>
                    <td className="px-3 py-3 text-center text-xs text-gray-700">{p.bound_count ?? 0}</td>
                    <td className="px-3 py-3 text-center text-xs">
                      <span className={clsx((p.used_count ?? 0) > 0 ? 'text-blue-600 font-medium' : 'text-gray-400')}>
                        {p.used_count ?? 0}
                      </span>
                      <span className="text-gray-300"> / {p.bound_count ?? 0}</span>
                    </td>
                    <td className="px-3 py-3 text-center text-xs text-gray-700">{p.candidate_count ?? 0}</td>
                    <td className="px-3 py-3 text-center text-xs">
                      <span className={clsx((p.offer_count ?? 0) > 0 ? 'text-amber-600 font-medium' : 'text-gray-400')}>
                        {p.offer_count ?? 0}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-500 whitespace-nowrap">{fmtDate(p.published_at)}</td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <button onClick={e => { e.stopPropagation(); onOpenPosition(p.id); }}
                        className="text-xs text-primary-600 hover:text-primary-700 font-medium">详情</button>
                      {isHr && (
                        <button onClick={e => { e.stopPropagation(); onOpenPosition(p.id, 'binding'); }}
                          className="ml-3 text-xs text-gray-500 hover:text-primary-600">编制绑定</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {filtered.length > 0 && (
            <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
              <span>共 {filtered.length} 条 · 第 {curPage}/{totalPages} 页</span>
              <div className="flex items-center gap-1">
                <button disabled={curPage <= 1} onClick={() => setPage(curPage - 1)}
                  className="h-7 w-7 flex items-center justify-center rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">
                  <ChevronLeft size={13} />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  // 页数多时只渲染当前页附近的页码，避免页脚被撑开
                  .filter(n => n === 1 || n === totalPages || Math.abs(n - curPage) <= 1)
                  .map((n, idx, arr) => (
                    <React.Fragment key={n}>
                      {idx > 0 && n - arr[idx - 1] > 1 && <span className="px-1 text-gray-300">…</span>}
                      <button onClick={() => setPage(n)}
                        className={clsx('h-7 min-w-[28px] px-1.5 rounded border text-xs',
                          n === curPage ? 'border-primary-500 text-primary-600 font-medium' : 'border-gray-200 hover:bg-gray-50')}>
                        {n}
                      </button>
                    </React.Fragment>
                  ))}
                <button disabled={curPage >= totalPages} onClick={() => setPage(curPage + 1)}
                  className="h-7 w-7 flex items-center justify-center rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">
                  <ChevronRight size={13} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                <AlertTriangle size={16} className="text-red-500" />
                确认关闭 {selectedIds.length} 个岗位？
              </h3>
              <button onClick={() => setShowConfirm(false)} disabled={closing}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-40"><X size={16} /></button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              {!preview ? (
                <div className="py-8 flex justify-center"><Loader2 size={20} className="animate-spin text-primary-500" /></div>
              ) : (
                <>
                  <p className="text-xs text-gray-600">
                    关闭后岗位不再进入招聘门户与候选人岗位下拉，已关联的候选人不受影响。
                  </p>
                  {preview.release_total > 0 && (
                    <div className="bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
                      <p className="text-xs font-medium text-emerald-700">将释放 {preview.release_total} 个编制，回到可用池</p>
                    </div>
                  )}
                  {preview.keep_total > 0 && (
                    <div className="bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                      <p className="text-xs font-medium text-amber-700">
                        {preview.keep_total} 个编制不会释放（Offer 流转中或候选人已入职占用），需先撤回 Offer 才能释放
                      </p>
                    </div>
                  )}
                  {/* 逐岗位列出编制明细，让 HR 关之前能核对到具体编号 */}
                  <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                    {preview.items.map(item => (
                      <div key={item.id} className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-gray-800 truncate">{item.name}</span>
                          <span className="text-[11px] text-gray-400">{item.code || '—'}</span>
                          <span className="text-[11px] text-gray-400 ml-auto">{item.status_label}</span>
                        </div>
                        {item.will_release.length > 0 && (
                          <p className="mt-1 text-[11px] text-emerald-600">
                            释放：{item.will_release.map(s => s.code).join('、')}
                          </p>
                        )}
                        {item.will_keep.length > 0 && (
                          <p className="mt-0.5 text-[11px] text-amber-600">
                            保留：{item.will_keep.map(s => `${s.code}（${s.status_label}）`).join('、')}
                          </p>
                        )}
                        {item.will_release.length === 0 && item.will_keep.length === 0 && (
                          <p className="mt-1 text-[11px] text-gray-400">无绑定编制</p>
                        )}
                      </div>
                    ))}
                    {preview.items.length === 0 && (
                      <p className="px-3 py-3 text-[11px] text-gray-400">编制影响预览加载失败，仍可继续关闭</p>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-2">
              <button onClick={() => setShowConfirm(false)} disabled={closing}
                className="h-9 px-4 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40">
                取消
              </button>
              <button onClick={runBatchClose} disabled={closing}
                className="h-9 px-4 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg flex items-center gap-1.5 disabled:opacity-60">
                {closing && <Loader2 size={13} className="animate-spin" />}
                {closing ? '关闭中...' : `确认关闭 ${selectedIds.length} 个岗位`}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDrawer && (
        <CreatePositionDrawer
          currentUser={currentUser}
          onClose={() => setShowDrawer(false)}
          onSaved={(saved, goBinding) => {
            setShowDrawer(false);
            onUpdate();
            toast.success('岗位草稿已保存');
            if (goBinding) onOpenPosition(saved.id, 'binding');
          }}
        />
      )}
    </div>
  );
}
